// ============================================================
// TERROR TCG — GILDED OBSIDIAN THEME v4  (July 5, 2026)
// Polished obsidian slabs, forged gold, silver filigree,
// amethyst starlight, rose gems — the Unstable World made chrome.
//
// v3 → v4: the warm vellum/manuscript browns are DEAD. UI chrome
// is now obsidian with a violet undertone. Gold stays the primary
// metal (Terror's DNA); silver joins as secondary ornament;
// amethyst is the ambient accent (rhyming with the SD board
// purples); rose marks hearts/vitals.
//
// COMPAT LAW: every GM key name from v3 survives with a new value,
// so all 600+ GM.* call sites recolor without a single code change.
// Semantic drift to know:
//   pageWarm  — no longer "warm"; it's the shallow obsidian step.
//   inkBrown  — no longer copper; it's violet-steel (muted borders).
// SD (board/cards/field) is UNTOUCHED — the palette law holds.
//
// NEW in v4:
//   GM.silver / silverBright / silverDim
//   GM.amethyst / amethystBright / amethystDim
//   GM.rose / roseDim
//   GM.candlelight (was referenced by Rulebook but never defined —
//                   now a real soft gold ambience gradient)
//   GM.fontBody (Cormorant reading voice) · GM.fontUI (crisp sans
//                for NUMBERS/badges/chips — engraved capitals were
//                never meant to render "1,500" at 11px)
//   MATERIAL LAYER: GOLD_METAL, SILVER_METAL, OBSIDIAN_SLAB(+LIT),
//   OBSIDIAN_RECESS, facet(), SHADOW_RAISED/FLOAT/RECESS,
//   edgeLight(), bloom(), textGlow(), TEXT_SET, OBSIDIAN_BG.
//
// THE MATERIAL LAWS (die, terminal, die):
//   1. No flat fills — every surface is a gradient with a light
//      source (glow above, depth below).
//   2. No 1px hairline borders — gold borders are METAL: gradient
//      strips with bright/dark alternation so light runs along them.
//   3. Faceted geometry — cut corners (facet()) on interactive
//      elements; the Unstable World is fractured, not rounded.
//      ⚠ clip-path clips box-shadows — use filter: drop-shadow for
//      outer depth on faceted elements; inset shadows still render.
//   4. Everything pressable has depth — edgeLight on top, shadow
//      below, hover = bloom, press = sink.
//   5. Active = LIGHT, not color-swap — selected things emit.
//   6. Ambient life — stardust, shimmer sweeps (ensureKeyframes
//      pattern only — never <style> blocks in components).
//   7. Text has presence — textGlow on golds, TEXT_SET on body.
// ============================================================

// ── Board / card / field colors — DO NOT use in UI chrome ────
export const SD = {
  void0: "#000001",
  void1: "#03020c",
  void2: "#07051a",
  void3: "#0d0a28",
  void4: "#1a1540",

  starWhite: "#f0e6d0",
  starSilver: "#d4c9b8",
  starDim: "#9a8a7a",

  gold: "#c9a84c",
  goldBright: "#f0d060",
  goldDim: "#4a3c18",

  purple: "#6d28d9",
  purpleLight: "#a78bfa",
  purpleDim: "#2d1b69",

  teal: "#0e7490",
  tealLight: "#22d3ee",
  tealDim: "#0a2830",

  red: "#dc2626",
  redDim: "#7f1d1d",
  redMist: "#991b1b",

  blue: "#3b82f6",
  blueDim: "#1e3a8a",

  fontDisplay: "'Cinzel', 'Palatino Linotype', serif",
  fontMono: "'Courier New', Courier, monospace",
};

// ── Gilded Obsidian Palette — use for ALL UI chrome ──────────
// Polished black glass with violet depths, forged gold, moonlit
// silver, amethyst starlight, rose gems.
export const GM = {
  // Obsidian steps — page → raised surface (deepest to lightest)
  page:          "#070410",       // the void-glass floor
  pageWarm:      "#0d0918",       // shallow obsidian (legacy name)
  pageMid:       "#150e26",       // raised slab
  pageLight:     "#1e1636",       // lit slab / hover surface
  pageBorder:    "#392a52",       // violet-steel edge line

  // Forged gold — Terror's primary metal (unchanged: the DNA)
  inkGold:       "#c8963c",       // forged gold — primary accent
  inkGoldBright: "#e8b84b",       // gold catching the light
  inkGoldDim:    "#8a6520",       // gold in shadow
  inkBrown:      "#6d6288",       // violet-steel — muted borders (legacy name)
  inkRed:        "#8b2020",       // blood rubric

  // Moonvellum text — cooled from candle-cream toward moonlight
  vellum:        "#ece2cc",       // main text
  vellumDim:     "#b3a68c",       // secondary
  vellumFaint:   "#7d7191",       // muted / disabled (violet-dust)

  // Silver filigree — secondary ornament metal (NEW)
  silver:        "#b8c0d4",
  silverBright:  "#e2e8f4",
  silverDim:     "#5d637a",

  // Amethyst starlight — ambient accent (NEW; rhymes with SD purple)
  amethyst:       "#8b5cf6",
  amethystBright: "#c084fc",
  amethystDim:    "#3b2a63",

  // Rose gem — hearts / vitals / affection (NEW)
  rose:          "#f472b6",
  roseDim:       "#9d4a76",

  // Decorative
  ruling:        "rgba(122,109,156,0.16)", // faint crystalline ruling
  candlelight:   "radial-gradient(ellipse at 50% 0%, rgba(232,184,75,0.10) 0%, rgba(139,92,246,0.05) 40%, transparent 70%)",

  font:     "'Cinzel', 'Palatino Linotype', serif",
  fontD:    "'Cinzel Decorative', 'Palatino Linotype', serif",
  fontBody: "'Cormorant Garamond', 'Palatino Linotype', serif",
  fontUI:   "'Inter', 'Segoe UI', system-ui, sans-serif",
};

// ── Card type colors — semantic, unchanged ────────────────────
export const TYPE_COLORS_SD = {
  "Escendant": "#f87171",
  "Tome":      "#22d3ee",
  "Primera":   "#fbbf24",
  "U'hlti":   "#c084fc",
  "Affinity":  "#f0d060",
  "Prophecy":  "#818cf8",
  "Curio":     "#fb923c",
  "Escling":   "#a8896a",
};

export const ILK_COLORS_SD = {
  "Space":   "#22d3ee",
  "Phantom": "#c084fc",
  "Dragon":  "#f87171",
  "Time":    "#fbbf24",
};

// ── Board background — for Shatterfield / walkable rooms ─────
export const STARFIELD_BG = `
  radial-gradient(ellipse at 25% 25%, rgba(109,40,217,0.35) 0%, rgba(109,40,217,0.08) 35%, transparent 60%),
  radial-gradient(ellipse at 75% 70%, rgba(153,27,27,0.28) 0%, rgba(153,27,27,0.06) 40%, transparent 65%),
  radial-gradient(ellipse at 60% 15%, rgba(109,40,217,0.20) 0%, transparent 45%),
  radial-gradient(ellipse at 15% 75%, rgba(220,38,38,0.18) 0%, transparent 40%),
  radial-gradient(ellipse at 85% 45%, rgba(14,116,144,0.12) 0%, transparent 35%),
  radial-gradient(ellipse at 50% 50%, rgba(45,27,105,0.25) 0%, transparent 70%),
  #000001
`;

// ── Obsidian page background — for all UI screens ────────────
// (Keeps the legacy MANUSCRIPT_BG export name so all screens
// recolor without touching a single call site.)
export const OBSIDIAN_BG = [
  "repeating-linear-gradient(135deg, transparent 0px, transparent 26px, rgba(192,132,252,0.028) 26px, rgba(192,132,252,0.028) 27px)",
  "radial-gradient(ellipse at 28% 0%, rgba(139,92,246,0.13) 0%, transparent 52%)",
  "radial-gradient(ellipse at 78% 92%, rgba(59,42,99,0.30) 0%, transparent 55%)",
  "radial-gradient(ellipse at 8% 80%, rgba(139,32,32,0.07) 0%, transparent 45%)",
  "#070410",
].join(", ");
export const MANUSCRIPT_BG = OBSIDIAN_BG;

// ═════════════════════════════════════════════════════════════
// THE MATERIAL LAYER — gradients, shadows, facets
// One-source recipes so every surface is forged from the same
// metal. Use these instead of hand-rolling per screen.
// ═════════════════════════════════════════════════════════════

// Metal strips — light running along a forged border. Use as the
// `background` of a 2–4px div strip, or as the outer layer of a
// padded-wrapper metal frame (outer div = metal bg + padding:2px,
// inner div = OBSIDIAN_SLAB).
export const GOLD_METAL =
  "linear-gradient(160deg, #f5d878 0%, #a06a1e 22%, #e8b84b 40%, #6b4310 62%, #f0cc60 82%, #8a5c18 100%)";
export const GOLD_METAL_H =
  "linear-gradient(90deg, transparent, #a06a1e 12%, #e8b84b 35%, #f5d878 50%, #e8b84b 65%, #a06a1e 88%, transparent)";
export const SILVER_METAL =
  "linear-gradient(160deg, #eef2fa 0%, #6d7488 22%, #c8d0e2 42%, #4a5064 64%, #dde4f2 84%, #5d637a 100%)";
export const SILVER_METAL_H =
  "linear-gradient(90deg, transparent, #6d7488 12%, #c8d0e2 35%, #eef2fa 50%, #c8d0e2 65%, #6d7488 88%, transparent)";

// Obsidian surfaces — never flat. Light source top-left.
export const OBSIDIAN_SLAB =
  "linear-gradient(175deg, #1e1636 0%, #150e26 55%, #0c0818 100%)";
export const OBSIDIAN_SLAB_LIT =
  "linear-gradient(175deg, #3a2a60 0%, #241a44 50%, #170f30 100%)";
export const OBSIDIAN_RECESS =
  "linear-gradient(175deg, #05030c 0%, #0d0918 60%, #120c20 100%)";
export const OBSIDIAN_PANEL =
  "radial-gradient(ellipse at 30% 0%, #2a1d4a 0%, #130c24 45%, #070410 100%)";

// Faceted geometry — cut-corner clip-paths. facet(n) cuts all four;
// facetTB(n) cuts top-left + bottom-right (the row/button shape).
// ⚠ clip-path clips box-shadow: pair with `filter: drop-shadow(...)`
//   for outer depth. Inset shadows still show.
export const facet = (n = 12) =>
  `polygon(${n}px 0, calc(100% - ${n}px) 0, 100% ${n}px, 100% calc(100% - ${n}px), calc(100% - ${n}px) 100%, ${n}px 100%, 0 calc(100% - ${n}px), 0 ${n}px)`;
export const facetTB = (n = 10) =>
  `polygon(${n}px 0, 100% 0, 100% calc(100% - ${n}px), calc(100% - ${n}px) 100%, 0 100%, 0 ${n}px)`;

// Shadow stacks — depth without thinking. RAISED for slabs/rows,
// FLOAT for modals/panels, RECESS for wells/inputs.
export const SHADOW_RAISED =
  "inset 0 1px 0 rgba(226,232,244,0.14), 0 3px 10px rgba(0,0,0,0.6)";
export const SHADOW_FLOAT =
  "inset 0 1px 0 rgba(245,216,120,0.28), 0 10px 34px rgba(0,0,0,0.75), 0 0 44px rgba(139,92,246,0.10)";
export const SHADOW_RECESS =
  "inset 0 2px 8px rgba(0,0,0,0.75), inset 0 -1px 0 rgba(226,232,244,0.06)";

// Light helpers
// edgeLight — a specular top edge on a pressable surface
export const edgeLight = (color = "#f5d878", a = 0.5) =>
  `inset 0 1px 0 rgba(${color === "#f5d878" ? "245,216,120" : "226,232,244"},${a})`;
// bloom — the ACTIVE glow (selected things emit)
export const bloom = (color = "#e8b84b") =>
  `0 0 14px ${color}66, 0 0 34px ${color}2e, inset 0 0 22px ${color}1f`;
// textGlow — gold/gem text presence
export const textGlow = (color = "#e8b84b") =>
  `0 0 10px ${color}88, 0 1px 2px #000`;
// TEXT_SET — body text sits ON the surface instead of floating
export const TEXT_SET = "0 1px 2px rgba(0,0,0,0.9)";

// ── Glow helpers (legacy signatures preserved) ────────────────
// shimmerBorder — for BOARD elements (cards, holos, combat UI)
export const shimmerBorder = (color = "#c9a84c") =>
  `0 0 0 1px ${color}99, 0 0 12px ${color}55, 0 0 24px ${color}28`;

// inkGlow — for chrome (panels, buttons, badges) — v4: deeper
export const inkGlow = (color = "#c8963c") =>
  `0 0 18px ${color}3c, inset 0 1px 0 ${color}2e`;
